import { useEffect, useState } from "react";
import { Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";

const STEP = 5;
const dollars = (cents: number | null) => (cents ? Math.round(cents / 100) : 0);
const usd = (n: number) => `$${n.toLocaleString("en-US")}`;

/** The button that shows the daily budget and opens its sheet, beside Launch. */
export function BudgetButton({ cents, onOpen }: { cents: number | null; onOpen: () => void }) {
  return (
    <button type="button" className="ad-budb" onClick={onOpen}>
      <span>Daily budget</span>
      <b>
        {cents ? `${usd(dollars(cents))} a day` : "Set it"}
        <Icon name="chev" />
      </b>
    </button>
  );
}

/**
 * Meta's daily budget in a sheet: the amount large, a step down or up, or typed with the number pad, and how
 * long the goal's money lasts at that pace. Saving it is the goal's next version.
 */
export function BudgetSheet({
  open,
  onClose,
  cents,
  totalCents,
  maxCents,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  cents: number | null;
  totalCents: number;
  /** The goal's own limit a day, when it has one. */
  maxCents: number | null;
  onSave: (cents: number) => Promise<boolean>;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setTyped(String(dollars(cents) || 30));
  }, [open, cents]);
  const n = Number(typed.replace(/[^0-9]/g, "")) || 0;
  const step = (by: number) => setTyped(String(Math.max(STEP, Math.round((n + by) / STEP) * STEP)));
  const over = maxCents !== null && n * 100 > maxCents;
  const save = async () => {
    if (!n || over) return;
    if (n * 100 === cents) return onClose();
    setBusy(true);
    const ok = await onSave(n * 100);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Daily budget"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      foot={
        <button type="button" className="pn-btn pri wide" disabled={!n || over || busy} onClick={() => void save()}>
          {n * 100 === cents ? "Keep" : "Set"} {usd(n)} a day
        </button>
      }
    >
      <div className="ad-bud">
        <button type="button" className="ad-sb lg" aria-label="Less" onClick={() => step(-STEP)}>
          −
        </button>
        <label className="ad-budv">
          <span className="ad-budn">
            <span>$</span>
            <input inputMode="numeric" value={typed} aria-label="Daily budget in dollars" onChange={(e) => setTyped(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} />
          </span>
          <span>a day</span>
        </label>
        <button type="button" className="ad-sb lg" aria-label="More" onClick={() => step(STEP)}>
          +
        </button>
      </div>
      <div className="pn-grp">
        <div className="pn-row static">
          <span className="m">
            <span className="t">Up to {usd(dollars(totalCents))} in total</span>
            {n > 0 && <span className="s">About {Math.max(1, Math.round(totalCents / (n * 100)))} days of ads at this pace</span>}
          </span>
        </div>
      </div>
      {over && maxCents !== null && <p className="pn-note ad-bad">The goal's limit is {usd(dollars(maxCents))} a day.</p>}
    </Sheet>
  );
}
